import {z} from "zod";
import {absMoney, addMoney, money, moneySchema, sumByCurrency, zeroMoney, type Money} from "./money.ts";
import {transactionEnrichmentSchema} from "./enrichment.ts";
import {financialMoodSchema} from "./mood.ts";
export type {Money} from "./money.ts";

export const accountTypes = ["depository", "credit", "loan", "investment", "other"] as const;
export const accountStatuses = ["active", "inactive", "restricted", "disconnected", "authorization_expired"] as const;
export const syncStatuses = ["never_synced", "queued", "syncing", "success", "partial", "error", "disconnected"] as const;
export const transactionStatuses = ["posted", "pending", "void", "unknown"] as const;
export const categorySources = ["provider", "rule", "model", "user"] as const;
export const refreshStatuses = ["pending", "succeeded", "failed", "unavailable"] as const;
export const syncStages = ["idle", "queued", "authorizing", "balances", "transactions", "finalizing", "complete", "failed"] as const;
export const financialProviders = ["plaid"] as const;

export type AccountType = (typeof accountTypes)[number];
export type AccountStatus = (typeof accountStatuses)[number];
export type SyncStatus = (typeof syncStatuses)[number];
export type TransactionStatus = (typeof transactionStatuses)[number];
export type CategorySource = (typeof categorySources)[number];
export type RefreshStatus = (typeof refreshStatuses)[number];
export type SyncStage = (typeof syncStages)[number];
export type FinancialProviderId = (typeof financialProviders)[number];

/** `createdAt` (ISO) lets analytics recognize the session that created the account. */
export interface SafeUser {
  id: string;
  email: string;
  displayName: string | null;
  createdAt?: string;
}

/**
 * Per-feature refresh state reported by the provider. `unavailable` means the
 * permission was never granted, which is different from a failed attempt.
 */
export const featureRefreshSchema = z.object({
  status: z.enum(refreshStatuses),
  lastAttemptedAt: z.string().datetime().nullable(),
  nextRefreshAvailableAt: z.string().datetime().nullable()
});
export type FeatureRefresh = z.infer<typeof featureRefreshSchema>;

export const accountRefreshStateSchema = z.object({
  balances: featureRefreshSchema,
  transactions: featureRefreshSchema
});
export type AccountRefreshState = z.infer<typeof accountRefreshStateSchema>;

export const accountNumberSchema = z.object({
  identifierType: z.string(),
  status: z.string(),
  supportedNetworks: z.array(z.string()),
  expectedExpiryDate: z.string().datetime().nullable()
});
export type AccountNumberDetail = z.infer<typeof accountNumberSchema>;

/** Provider explanation for an account that is losing or has lost access. */
export const accountStatusDetailSchema = z.object({
  action: z.enum(["none", "relink_required"]),
  cause: z.enum(["access_expired", "institution_requirement", "unspecified"]),
  expectedDeactivationAt: z.string().datetime().nullable()
});
export type AccountStatusDetail = z.infer<typeof accountStatusDetailSchema>;

/**
 * Observable progress for one account's synchronization. `percent` is derived
 * from completed stages so the client never has to guess a value.
 */
export const syncProgressSchema = z.object({
  stage: z.enum(syncStages),
  percent: z.number().int().min(0).max(100),
  message: z.string().max(200),
  startedAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime().nullable(),
  transactionCount: z.number().int().nonnegative(),
  error: z.object({code: z.string().max(120), message: z.string().max(400)}).nullable()
});
export type SyncProgress = z.infer<typeof syncProgressSchema>;

export const syncStageOrder: readonly SyncStage[] = ["queued", "authorizing", "balances", "transactions", "finalizing", "complete"];

export function progressPercentFor(stage: SyncStage): number {
  if (stage === "idle") return 0;
  if (stage === "failed") return 100;
  const index = syncStageOrder.indexOf(stage);
  if (index < 0) return 0;
  return Math.round(((index + 1) / syncStageOrder.length) * 100);
}

export function idleSyncProgress(): SyncProgress {
  return {stage: "idle", percent: 0, message: "Not synchronized yet", startedAt: null, updatedAt: null, transactionCount: 0, error: null};
}

export const institutionSchema = z.object({
  id: z.string(),
  providerId: z.string(),
  name: z.string(),
  logoUrl: z.string().url().nullable(),
  status: z.enum(["connected", "syncing", "error", "disconnected"]),
  lastSyncedAt: z.string().datetime().nullable(),
  accounts: z.array(z.lazy(() => accountSchema))
});

export const accountSchema = z.object({
  id: z.string(),
  providerId: z.string(),
  institutionId: z.string(),
  institutionName: z.string(),
  name: z.string(),
  officialName: z.string().nullable(),
  type: z.enum(accountTypes),
  category: z.string(),
  subtype: z.string().nullable(),
  mask: z.string().nullable(),
  status: z.enum(accountStatuses),
  statusDetail: accountStatusDetailSchema.nullable(),
  syncStatus: z.enum(syncStatuses),
  syncProgress: syncProgressSchema,
  currency: z.string().regex(/^[A-Z]{3}$/),
  balanceType: z.enum(["cash", "credit", "unknown"]),
  currentBalance: moneySchema.nullable(),
  availableBalance: moneySchema.nullable(),
  creditUsed: moneySchema.nullable(),
  balanceObservedAt: z.string().datetime().nullable(),
  lastSyncedAt: z.string().datetime().nullable(),
  connectedAt: z.string().datetime().nullable(),
  livemode: z.boolean(),
  permissions: z.array(z.string()),
  subscriptions: z.array(z.string()),
  supportedPaymentMethodTypes: z.array(z.string()),
  accountNumbers: z.array(accountNumberSchema),
  accountHolderType: z.enum(["account", "customer", "unknown"]),
  refreshState: accountRefreshStateSchema,
  capabilities: z.object({
    balances: z.boolean(),
    transactions: z.boolean()
  })
});

export type Institution = z.infer<typeof institutionSchema>;
export type Account = z.infer<typeof accountSchema>;

export const transactionSchema = z.object({
  id: z.string(),
  providerId: z.string(),
  accountId: z.string(),
  /** Settlement date when the provider reports one, otherwise the transaction date. */
  postedAt: z.string().datetime(),
  /** Date the transaction occurred at the merchant, before settlement. */
  authorizedAt: z.string().datetime().nullable(),
  voidedAt: z.string().datetime().nullable(),
  providerUpdatedAt: z.string().datetime().nullable(),
  /** Provider refresh token that produced this row; used as an incremental cursor. */
  transactionRefreshId: z.string().nullable(),
  /** Signed amount in integer minor units. Negative is money leaving. */
  amountMinor: z.number().int(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  description: z.string(),
  /**
   * Deterministic cleanup of `description` used for grouping. It is derived
   * from provider text only and never replaces a provider-supplied merchant.
   */
  normalizedDescription: z.string(),
  merchantName: z.string().nullable(),
  merchantCategory: z.string().nullable(),
  category: z.string().nullable(),
  categorySource: z.enum(categorySources).nullable(),
  categoryConfidence: z.number().min(0).max(1).nullable(),
  /**
   * Set when this row is one leg of a movement between the person's own
   * accounts. Both legs share `transferGroupId` and are excluded from income
   * and spending so a transfer is never counted as either.
   */
  isInternalTransfer: z.boolean(),
  transferGroupId: z.string().nullable(),
  /** Links rows the recurrence detector grouped into one repeating charge. */
  recurrenceId: z.string().nullable(),
  status: z.enum(transactionStatuses),
  pending: z.boolean(),
  livemode: z.boolean(),
  /**
   * Structured facts the enrichment model derived from provider text:
   * normalized company, country, channel, subscription flag, tags. Absent
   * until the enrichment pass has run for this row.
   */
  enrichment: transactionEnrichmentSchema.nullish()
});
export type Transaction = z.infer<typeof transactionSchema>;

export function transactionAmount(transaction: Pick<Transaction, "amountMinor" | "currency">): Money {
  return money(transaction.amountMinor, transaction.currency);
}

/** Rows that count toward income and spending: settled, and not a self-transfer. */
export function isAnalysable(transaction: Pick<Transaction, "status" | "pending" | "isInternalTransfer">): boolean {
  return transaction.status === "posted" && !transaction.pending && !transaction.isInternalTransfer;
}

export const balanceHistoryPointSchema = z.object({
  accountId: z.string(),
  observedAt: z.string().datetime(),
  currentBalance: moneySchema.nullable(),
  availableBalance: moneySchema.nullable(),
  creditUsed: moneySchema.nullable().default(null),
  balanceType: z.enum(["cash", "credit", "unknown"]).default("unknown")
});
export type BalanceHistoryPoint = z.infer<typeof balanceHistoryPointSchema>;

export const connectionSchema = z.object({
  id: z.string(),
  providerSessionId: z.string(),
  status: z.enum(["created", "pending", "completed", "error", "disconnected"]),
  requestedPermissions: z.array(z.string()),
  createdAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable(),
  errorCode: z.string().nullable()
});
export type Connection = z.infer<typeof connectionSchema>;

export const bankOverviewSchema = z.object({
  institutions: z.array(institutionSchema),
  totalAccounts: z.number().int().nonnegative(),
  totalCurrentBalance: z.array(moneySchema),
  lastUpdatedAt: z.string().datetime().nullable(),
  dataSource: z.enum(["live", "sandbox", "unconfigured", "sample"])
});
export type BankOverview = z.infer<typeof bankOverviewSchema>;

export const chartTypeSchema = z.enum(["line", "bar", "area", "pie", "donut"]);
export type ChartType = z.infer<typeof chartTypeSchema>;

const chartValueSchema = z.union([z.string(), z.number()]);
const chartSeriesSchema = z.object({key: z.string().min(1).max(64), label: z.string().min(1).max(80)});
export const chartSchema = z
  .object({
    type: z.literal("chart"),
    chartType: chartTypeSchema,
    title: z.string().min(1).max(160),
    xKey: z.string().min(1).max(64),
    series: z.array(chartSeriesSchema).min(1).max(8),
    data: z.array(z.record(z.string(), chartValueSchema)).max(500),
    currency: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .nullable()
      .default(null)
  })
  .superRefine((chart, context) => {
    const seenSeries = new Set<string>();
    chart.series.forEach((series, index) => {
      if (seenSeries.has(series.key)) context.addIssue({code: z.ZodIssueCode.custom, path: ["series", index, "key"], message: "Series keys must be unique"});
      seenSeries.add(series.key);
    });
    chart.data.forEach((row, rowIndex) => {
      if (!Object.hasOwn(row, chart.xKey)) context.addIssue({code: z.ZodIssueCode.custom, path: ["data", rowIndex, chart.xKey], message: "Chart rows must include the x-axis key"});
      for (const series of chart.series) if (!Object.hasOwn(row, series.key)) context.addIssue({code: z.ZodIssueCode.custom, path: ["data", rowIndex, series.key], message: "Chart rows must include every series key"});
    });
  });
export type ChartSpec = z.infer<typeof chartSchema>;

export const tableSchema = z.object({
  type: z.literal("table"),
  title: z.string().min(1).max(160),
  columns: z
    .array(z.object({key: z.string().min(1), label: z.string().min(1)}))
    .min(1)
    .max(12),
  rows: z.array(z.record(z.string(), chartValueSchema)).max(500)
});
export type TableSpec = z.infer<typeof tableSchema>;

export const metricSchema = z.object({
  type: z.literal("metric"),
  label: z.string().min(1).max(100),
  value: z.string().min(1).max(100),
  detail: z.string().max(240).nullable().default(null)
});
export type MetricSpec = z.infer<typeof metricSchema>;

/** A highlighted takeaway with a tone the UI renders as a coach callout. */
export const insightSchema = z.object({
  type: z.literal("insight"),
  tone: z.enum(["positive", "neutral", "warning", "alert"]),
  title: z.string().min(1).max(120),
  body: z.string().min(1).max(600)
});
export type InsightSpec = z.infer<typeof insightSchema>;

export const responsePartSchema = z.union([z.object({type: z.literal("text"), text: z.string().max(12000)}), metricSchema, tableSchema, chartSchema, insightSchema, z.object({type: z.literal("source"), label: z.string().max(120), range: z.string().max(240)})]);
export type ResponsePart = z.infer<typeof responsePartSchema>;

export const chatResponseSchema = z.object({
  parts: z.array(responsePartSchema).min(1).max(20),
  sourceAccountIds: z.array(z.string()).max(100),
  sourceRange: z.object({from: z.string().datetime().nullable(), to: z.string().datetime().nullable()}),
  suggestedQuestions: z.array(z.string().max(200)).max(5),
  /** Coach mood computed from account state when this answer was produced. */
  mood: financialMoodSchema.nullish()
});
export type ChatResponse = z.infer<typeof chatResponseSchema>;

export const chatMessageInputSchema = z.object({
  content: z.string().trim().min(1).max(4000),
  conversationId: z.string().min(1).optional(),
  stream: z.boolean().optional().default(false),
  generationId: z.string().trim().min(1).max(100).optional()
});
/** Client-side request shape; `stream` is optional because the SSE endpoint defaults to JSON. */
export type ChatMessageInput = z.input<typeof chatMessageInputSchema>;

export const chatStopInputSchema = z.object({generationId: z.string().trim().min(1).max(100).optional()});
export type ChatStopInput = z.infer<typeof chatStopInputSchema>;

export const chatStreamStatuses = ["analyzing", "reading_accounts", "querying_transactions", "computing", "rendering"] as const;
export type ChatStreamStatus = (typeof chatStreamStatuses)[number];

export const chatStreamEventSchema = z.discriminatedUnion("type", [
  z.object({type: z.literal("status"), status: z.enum(chatStreamStatuses), detail: z.string().max(160).nullable().default(null)}),
  z.object({type: z.literal("tool"), name: z.string().max(80), label: z.string().max(160)}),
  z.object({type: z.literal("delta"), text: z.string().max(4000)}),
  z.object({type: z.literal("part"), part: responsePartSchema}),
  z.object({type: z.literal("complete"), conversationId: z.string().min(1), response: chatResponseSchema}),
  z.object({type: z.literal("stopped")}),
  z.object({type: z.literal("error"), code: z.string().max(80), message: z.string().max(240)})
]);
export type ChatStreamEvent = z.infer<typeof chatStreamEventSchema>;

export const appleSignInSchema = z.object({
  challenge: z.string().min(1).max(1024),
  authorizationCode: z.string().min(1).max(4096),
  displayName: z.string().trim().max(160).optional()
});
export type AppleSignIn = z.infer<typeof appleSignInSchema>;

export const passwordSignInSchema = z.object({
  email: z.string().trim().email("Enter a valid email address.").max(254),
  password: z.string().min(1, "Enter your password.").max(256)
});
export type PasswordSignIn = z.infer<typeof passwordSignInSchema>;

export const passwordRegistrationSchema = passwordSignInSchema.extend({
  password: z.string().min(10, "Use at least 10 characters.").max(256),
  displayName: z.string().trim().max(160).optional()
});
export type PasswordRegistration = z.infer<typeof passwordRegistrationSchema>;

/** WorkOS emails a six-digit code to confirm a new email before the first sign-in completes. */
export const emailVerificationSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, "Enter the 6-digit code from the email."),
  pendingAuthenticationToken: z.string().min(1).max(512)
});
export type EmailVerification = z.infer<typeof emailVerificationSchema>;

export const forgotPasswordSchema = passwordSignInSchema.pick({email: true});
export type ForgotPassword = z.infer<typeof forgotPasswordSchema>;

export const passwordResetSchema = z.object({
  token: z.string().min(1).max(512),
  password: z.string().min(10, "Use at least 10 characters.").max(256)
});
export type PasswordReset = z.infer<typeof passwordResetSchema>;

/** One-time code a delegated surface, such as the ChatGPT embed, exchanges for a session. */
export const sessionHandoffSchema = z.object({code: z.string().trim().min(1).max(200)});
export type SessionHandoff = z.infer<typeof sessionHandoffSchema>;
/** The plugin's "Open Sprout" link; `confirm` is the person accepting the account it opens. */
export const linkHandoffSchema = sessionHandoffSchema.extend({confirm: z.boolean().optional()});
export type LinkHandoff = z.infer<typeof linkHandoffSchema>;

/** WorkOS AuthKit sends ChatGPT's connection to Sprout's sign-in page with this ID. */
export const connectCompletionSchema = z.object({externalAuthId: z.string().trim().min(1).max(128)});
export type ConnectCompletion = z.infer<typeof connectCompletionSchema>;

export const connectionActionSchema = z.object({
  institutionId: z.string().min(1).optional(),
  accountId: z.string().min(1).optional()
});

export const connectionStartSchema = z.object({
  provider: z.enum(financialProviders).optional(),
  platform: z.enum(["web", "ios"]).optional()
});

export const plaidLinkResultSchema = z.object({
  publicToken: z.string().trim().min(1).max(2048).nullable(),
  linkSessionId: z.string().trim().min(1).max(255),
  institution: z.object({id: z.string().trim().min(1).max(255), name: z.string().trim().min(1).max(255)}).nullable()
});
export type PlaidLinkResult = z.infer<typeof plaidLinkResultSchema>;

export const connectionCompletionSchema = z.object({
  providerSessionId: z.string().trim().min(1).max(255),
  provider: z.enum(financialProviders).optional(),
  result: plaidLinkResultSchema.optional()
});

export const dataDeletionSchema = z.object({confirmation: z.literal("DELETE_MY_ACCOUNT")});
export type DataDeletion = z.infer<typeof dataDeletionSchema>;

export type ConnectionAction = z.infer<typeof connectionActionSchema>;
export type ConnectionStart = z.infer<typeof connectionStartSchema>;
export type ConnectionCompletion = z.infer<typeof connectionCompletionSchema>;
export type ConnectionReconnect = ConnectionAction & ConnectionStart;

export const internalSyncRequestSchema = z.object({
  userId: z.string().min(1).max(100),
  syncRunId: z.string().min(1).max(100),
  institutionId: z.string().min(1).max(255).optional(),
  accountId: z.string().min(1).max(255).optional()
});
export type InternalSyncRequest = z.infer<typeof internalSyncRequestSchema>;

export function totalCurrentBalance(accounts: readonly Account[]): Money[] {
  return sumByCurrency(accounts.flatMap((account) => (account.currentBalance ? [account.currentBalance] : [])));
}

export function categoryOf(transaction: Pick<Transaction, "category" | "merchantCategory"> & {enrichment?: Transaction["enrichment"]}): string {
  // `category` is canonical because user corrections are stored there and
  // outrank the model. Enrichment remains a fallback for legacy rows.
  return transaction.category ?? transaction.enrichment?.category ?? transaction.merchantCategory ?? "Uncategorized";
}

export interface CategoryTotal {
  category: string;
  total: Money;
  count: number;
}

/**
 * Totals spending per category. Self-transfers and voided rows are excluded so
 * moving money between your own accounts never registers as spending.
 */
export function groupTransactionsByCategory(transactions: readonly Transaction[]): CategoryTotal[] {
  const grouped = new Map<string, {total: Money; count: number}>();
  for (const transaction of transactions) {
    if (!isAnalysable(transaction)) continue;
    const key = categoryOf(transaction);
    const current = grouped.get(key) ?? {total: zeroMoney(transaction.currency), count: 0};
    grouped.set(key, {total: addMoney(current.total, absMoney(transactionAmount(transaction))), count: current.count + 1});
  }
  return [...grouped.entries()].map(([category, value]) => ({category, total: value.total, count: value.count})).sort((a, b) => b.total.minorUnits - a.total.minorUnits);
}

export type Direction = "income" | "expense" | "all";

export function matchesDirection(transaction: Pick<Transaction, "amountMinor">, direction: Direction): boolean {
  if (direction === "income") return transaction.amountMinor > 0;
  if (direction === "expense") return transaction.amountMinor < 0;
  return true;
}

/**
 * Sums in integer minor units. Expenses are returned as a positive magnitude
 * because that is how people talk about what they spent.
 */
export function sumTransactions(transactions: readonly Transaction[], direction: Direction = "all", currency?: string): Money {
  const relevant = transactions.filter((transaction) => isAnalysable(transaction) && matchesDirection(transaction, direction));
  const resolved = currency ?? relevant[0]?.currency ?? transactions[0]?.currency ?? "USD";
  const total = relevant.reduce<Money>((sum, transaction) => addMoney(sum, transactionAmount(transaction)), zeroMoney(resolved));
  return direction === "expense" ? absMoney(total) : total;
}

const accountLabels: Record<string, string> = {
  checking: "Checking",
  savings: "Savings",
  credit_card: "Credit card",
  line_of_credit: "Line of credit",
  mortgage: "Mortgage",
  cash: "Cash",
  credit: "Credit",
  investment: "Investment",
  other: "Account"
};

/**
 * Builds a human label from provider fields only. When the institution does not
 * supply a display name we describe the account from its category and mask
 * rather than inventing a product name.
 */
export function accountDisplayName(account: Pick<Account, "name" | "subtype" | "category" | "mask">): string {
  if (account.name.trim().length > 0) return account.name;
  const base = accountLabels[account.subtype ?? ""] ?? accountLabels[account.category] ?? "Account";
  return account.mask ? `${base} ••${account.mask}` : base;
}

const stageMessages: Record<SyncStage, string> = {
  idle: "Not synchronized yet",
  queued: "Waiting for a sync slot",
  authorizing: "Confirming authorization",
  balances: "Reading balances",
  transactions: "Downloading transactions",
  finalizing: "Finishing up",
  complete: "Up to date",
  failed: "Sync failed"
};

export function syncStageMessage(stage: SyncStage): string {
  return stageMessages[stage];
}

export function progressFor(stage: SyncStage, overrides: Partial<SyncProgress> = {}): SyncProgress {
  const now = new Date().toISOString();
  return {
    stage,
    percent: progressPercentFor(stage),
    message: overrides.message ?? syncStageMessage(stage),
    startedAt: overrides.startedAt ?? now,
    updatedAt: now,
    transactionCount: overrides.transactionCount ?? 0,
    error: overrides.error ?? null
  };
}

/**
 * Net position across accounts. Provider adapters normalize liabilities to
 * negative balances before records reach this aggregation.
 */
export function netPositionByCurrency(accounts: readonly Account[]): Money[] {
  return sumByCurrency(accounts.flatMap((account) => (account.status === "disconnected" || !account.currentBalance ? [] : [account.currentBalance])));
}

/** True when the account still needs the holder to re-authorize at the bank. */
export function requiresReconnect(account: Pick<Account, "status" | "statusDetail">): boolean {
  if (account.status === "authorization_expired") return true;
  return account.statusDetail?.action === "relink_required";
}

const descriptorNoise = [/\b(?:x{2,}|\*{2,})\d+\b/gi, /\b\d{2}\/\d{2}(?:\/\d{2,4})?\b/g, /\b(?:card|acct|account|ref|id|trace|seq|ppd|ccd|web|tel|pos|ach|des|indn|co)\s*[:#]?\s*[a-z0-9-]{4,}\b/gi, /\b[a-z0-9]{12,}\b/gi, /#\s*\d+/g];

/**
 * Reduces a raw bank description to a stable grouping key. The result is only
 * ever a cleaned form of text the institution already sent.
 */
export function normalizeTransactionDescriptor(description: string): string {
  let value = description.toLowerCase();
  for (const pattern of descriptorNoise) value = value.replace(pattern, " ");
  value = value
    .replace(/[^a-z0-9&' ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const words = value
    .split(" ")
    .filter((word) => word.length > 1)
    .slice(0, 4);
  const label = words.join(" ").trim();
  const source = label.length > 0 ? label : description.trim().toLowerCase();
  return source.replace(/\b[a-z]/g, (letter) => letter.toUpperCase()).slice(0, 80) || "Unknown";
}
